import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Globe, MessageSquare, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { previewApi } from "@/api/browser.ts";
import { chatsApi } from "@/api/chats.ts";
import { cn } from "@/lib/cn.ts";
import { HealthCenter } from "./HealthCenter.tsx";
import { Button } from "@/ui/primitives.tsx";
import { projectQuery } from "./ProjectPage.tsx";

/** The original's project home: "Workspace — name", recent chats, and what is listening inside. */
export function ProjectHome({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const nav = useNavigate();
  const p = useQuery(projectQuery(projectId)).data;
  const chats = useQuery({ queryKey: ["chats", projectId, "list"], queryFn: () => chatsApi.list(projectId) });
  const apps = useQuery({ queryKey: ["projects", projectId, "apps"], queryFn: () => previewApi.apps(projectId), enabled: p?.state === "running", refetchInterval: 10_000 });
  const create = useMutation({ mutationFn: () => chatsApi.create(projectId, {}), onSuccess: (c) => { void qc.invalidateQueries({ queryKey: ["chats", projectId, "list"] }); void nav({ to: "/projects/$projectId/chat/$chatId", params: { projectId, chatId: c.id } }); } });
  const openExternal = async (port: number) => {
    const w = window.open("about:blank", "_blank");
    try {
      const e = await previewApi.entry(projectId, port);
      if (w) w.location.href = e.url;
    } catch {
      w?.close();
    }
  };
  if (!p) return null;
  const rel = (iso: string) => { const d = (Date.now() - new Date(iso).getTime()) / 1000; const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" }); return d < 3600 ? rtf.format(-Math.round(d / 60), "minute") : d < 86400 ? rtf.format(-Math.round(d / 3600), "hour") : rtf.format(-Math.round(d / 86400), "day"); };
  const canEdit = p.myRole !== "viewer";
  return (
    <div className="relative mx-auto max-w-3xl py-10">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,_color-mix(in_oklch,var(--color-accent)_12%,transparent),_transparent_60%)]" />
      <h1 className="text-4xl font-semibold tracking-tight">{t("workspace.title")} <span className="text-accent">{p.name}</span></h1>
      <div dir="ltr" className="mt-3 flex items-center gap-4 font-mono text-sm text-fg-muted">
        <span className="flex items-center gap-2"><span className={cn("size-2 rounded-full", p.state === "running" ? "bg-accent" : p.state === "error" ? "bg-danger" : "bg-fg-muted/50")} />{t(`projects.state.${p.state}`)}</span>
        <span>{t("workspace.chats", { count: chats.data?.length ?? 0 })}</span>
        <span>{p.slug}</span>
      </div>

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-fg-muted">{t("chat.continue")}</h2>
          {canEdit && <Button size="sm" className="size-8 p-0" aria-label={t("chat.new")} onClick={() => create.mutate()} loading={create.isPending}><Plus className="size-4" /></Button>}
        </div>
        <div className="mt-3 space-y-2">
          {chats.data?.length === 0 && <p className="text-sm text-fg-muted">{t("chat.emptyProject")}</p>}
          {(chats.data ?? []).slice(0, 6).map((c) => (
            <Link key={c.id} to="/projects/$projectId/chat/$chatId" params={{ projectId, chatId: c.id }} className="flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 hover:border-accent/50">
              <span className="grid size-9 place-items-center rounded-lg bg-surface-2"><MessageSquare className="size-4 text-fg-muted" /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{c.title}</span>
                <span className="block text-xs text-fg-muted">Claude{c.model !== "default" ? ` · ${c.model}` : ""}{c.status === "running" ? ` · ${t("chat.working")}` : ""}</span>
              </span>
              <span className="text-xs text-fg-muted">{rel(c.updatedAt)}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-fg-muted">{t("workspace.runningHere")}</h2>
          <button type="button" aria-label={t("git.refresh")} className="rounded-full border border-border p-1 text-fg-muted hover:text-fg" onClick={() => void apps.refetch()}><RefreshCw className={cn("size-3.5", apps.isFetching && "animate-spin")} /></button>
        </div>
        <div className="mt-3 overflow-hidden rounded-xl border border-border bg-surface">
          {p.state !== "running" && <p className="p-4 text-sm text-fg-muted">{t("errors.project_not_running")}</p>}
          {p.state === "running" && apps.data?.length === 0 && <p className="p-4 text-sm text-fg-muted">{t("browser.noApps")}</p>}
          {(apps.data ?? []).map((a) => (
            <div key={a.port} className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0">
              <span className="grid size-9 place-items-center rounded-full bg-accent-soft"><Globe className="size-4 text-accent" /></span>
              <div dir="ltr" className="min-w-0 flex-1 font-mono">
                <div className="text-base">:{a.port}</div>
                <div className="truncate text-xs text-fg-muted">{a.loopback ? "127.0.0.1" : "0.0.0.0"} · {a.process.split(" ")[0]}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => void openExternal(a.port)}><ExternalLink className="size-3.5" /> {t("workspace.openTab")}</Button>
            </div>
          ))}
        </div>
      </section>

      <HealthCenter projectId={projectId} running={p.state === "running"} />
    </div>
  );
}
